import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  ViewChildren,
  OnInit,
  QueryList,
  AfterViewInit,
  AfterViewChecked,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormGroupDirective, FormControl } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute, UrlSerializer } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule, MatChipListboxChange, MatChipSelectionChange } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule, MatDatepicker } from '@angular/material/datepicker';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatOptionModule, MatNativeDateModule } from '@angular/material/core';
import isEqual from 'lodash/isEqual';

import { Subject, debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs';
import { Store } from '@ngxs/store';

// Actions
import { LoadAnalytics, ResetAnalytics } from '@app/store/app/states/analytics/analytics.actions';

// Enums
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { CountryDescriptor, Country, CountriesByContinent } from '@app/enums/country';
import { ContinentDescriptor, Continent } from '@app/enums/continent';
import { EntitySizeDescriptor, ESAdditionalInformationDescriptor } from '@app/enums/entity-size';
import { InputType } from '@app/enums/shared/input-type';

import {
  mapAnalyticsFiltersToIncidentsPageRouteParams,
  mapAnalyticsFiltersToRouteParams,
  mapRouteParamsToAnalyticsFilters,
} from '@app/utils/incidents/incidents.utils';

// Models
import {
  AnalyticsComparisonModel,
  AnalyticsModel,
  AnalyticsTableDataModel,
  AnalyticsTableDataRow,
} from '@app/models/analytics.model';

// Store
import { IncidentAnalyticsState } from '@app/store/app/states/analytics/analytics.state';
import { defaultAnalyticsSearchFilters, AnalyticsFilters, AnalyticsTimePeriod } from '../../types/analytics-filters';

// Utils
import { createAnalyticsComparisonData, buildContinents } from '@app/utils/incidents/incidents.utils';
import { mapEnumDescriptorToMultiselectOptions, sortAlphabetical } from '@app/utils/input-fields/input-fields.utils';
import {
  createDate,
  formatAsDateTimeStringIgnoringTheTime,
  formatAsYearAndMonthOnlyString,
} from '@app/utils/datetime/datetime.utils';
import { blockInvalidNumericSymbol } from '@app/shared/numeric-validations';
import { IncidentStatus } from '@app/enums/incident-status';

@Component({
  standalone: true,
  selector: 'app-incidents-list-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatTableModule,
    MatSortModule,
    MatOptionModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
  ],
  templateUrl: './analytics-dashboard-page.component.html',
  styleUrl: './analytics-dashboard-page.component.scss',
})
export class AnalyticsDashboardPageComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  protected readonly IncidentStatus = IncidentStatus;
  protected blockInvalidNumericSymbol = blockInvalidNumericSymbol;

  protected CountryDescriptor = CountryDescriptor;
  continents: {
    name: Continent;
    label: string;
    countries: Country[];
    continentAdjustedCountries: string[];
  }[] = [];

  protected filterGroup: FormGroup;
  protected filters: AnalyticsFilters = structuredClone(defaultAnalyticsSearchFilters);
  protected filterButtonDisabled = true;
  protected viewTable: 'entity' | 'country' | 'continent' = 'entity';

  protected get uiTimeRangeA(): { timePeriod: AnalyticsTimePeriod; dates: string } {
    return { timePeriod: 'A', dates: this.viewPeriods.rangeA };
  }

  protected get uiTimeRangeB(): { timePeriod: AnalyticsTimePeriod; dates: string } {
    return { timePeriod: 'B', dates: this.viewPeriods.rangeB };
  }

  protected get countryCodesControl(): FormControl<Country[]> {
    return this.filterGroup.get('countryCodes') as FormControl<Country[]>;
  }

  protected analyticsGenericTableColumns: string[] = [
    'incidentCountA',
    'incidentCountB',
    'ratioNumeric',
    'ratioProgress',
  ];

  protected analyticsEntityTableColumns: string[] = [];
  protected analyticsCountryTableColumns: string[] = [];
  protected analyticsContinentTableColumns: string[] = [];

  protected analyticsTableData?: AnalyticsComparisonModel;
  protected analyticsViewData?: AnalyticsTableDataModel;
  protected dataSource = new MatTableDataSource<AnalyticsTableDataRow>();

  protected analytics: AnalyticsModel = {
    analyticsPeriodA: void 0,
    analyticsPeriodB: void 0,
  };

  protected viewPeriods = {
    rangeA: '',
    rangeB: '',
  };

  // Dropdown options formatters
  protected continentOptions = mapEnumDescriptorToMultiselectOptions(ContinentDescriptor);
  protected countryOptions = mapEnumDescriptorToMultiselectOptions(CountryDescriptor);
  protected businessSectorOptions = sortAlphabetical(mapEnumDescriptorToMultiselectOptions(BusinessSectorDescriptor));
  protected entitySizeOptions = mapEnumDescriptorToMultiselectOptions(EntitySizeDescriptor);
  protected entityTooltipDescriptor = Object.keys(ESAdditionalInformationDescriptor)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((k) => ({ [k]: (ESAdditionalInformationDescriptor as any)[k] }))
    .reduce((acc, next) => ({ ...acc, ...next }), {});

  protected pageSize = 10;
  protected totalEntries = 0;
  protected pageNumber = 0;

  private destroy$ = new Subject<void>();

  @ViewChild('entitySort') entitySort?: MatSort;
  @ViewChild('countrySort') countrySort?: MatSort;
  @ViewChild('continentSort') continentSort?: MatSort;
  @ViewChildren('matOptgroupLabel', { read: ElementRef })
  protected labelGroups!: QueryList<ElementRef>;

  constructor(
    private cdr: ChangeDetectorRef,
    private store: Store,
    private router: Router,
    private urlSerializer: UrlSerializer,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) {
    this.filterGroup = this.fb.group({
      timeperiod1: this.fb.group({
        dateStart: [null],
        dateEnd: [null],
      }),
      timeperiod2: this.fb.group({
        dateStart: [null],
        dateEnd: [null],
      }),
      revenue: this.fb.group({
        start: [null],
        end: [null],
      }),
      businessSectors: [],
      entitySizes: [],
      countryCodes: [],
      continentCodes: [],
    });

    this.analyticsEntityTableColumns = ['businessSectorLabel', ...this.analyticsGenericTableColumns];
    this.analyticsCountryTableColumns = ['countryLabel', ...this.analyticsGenericTableColumns];
    this.analyticsContinentTableColumns = ['continentLabel', ...this.analyticsGenericTableColumns];

    this.onCheckboxLabelClick = this.onCheckboxLabelClick.bind(this);
  }

  public ngOnInit(): void {
    this.continents = buildContinents();

    this.store
      .select(IncidentAnalyticsState.getIncidentAnalytics)
      .pipe(takeUntil(this.destroy$))
      .subscribe((analytics) => {
        this.analytics = analytics;
        this.analyticsTableData = createAnalyticsComparisonData(analytics);
        this.analyticsViewData = this.analyticsTableData?.[this.viewTable] ?? [];

        // Initialize paginator
        this.totalEntries = this.analyticsViewData?.length ?? 0;
        this.pageNumber = 0;

        const startIndex = this.pageNumber * this.pageSize;
        this.dataSource.data = this.analyticsViewData.slice(startIndex, startIndex + this.pageSize);

        this.onTableViewChange({ value: 'entity' } as MatChipListboxChange);
      });

    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        filter((params) => Object.keys(params).length > 0),
        distinctUntilChanged((a, b) => isEqual(a, b)), // Also emits when page gets new data - this way we hold it
        tap((params) => {
          const { filters } = mapRouteParamsToAnalyticsFilters(params);

          // There is no need to reassign page numbers here this is done above
          this.patchFilterGroup();

          // Finally trigger the event
          this.store.dispatch(new LoadAnalytics({ filters }));
        }),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    // Update filters when first loading (or reloading) the page
    const { filters } = mapRouteParamsToAnalyticsFilters(this.route.snapshot.queryParams);
    this.filters = filters;

    this.patchFilterGroup();

    this.labelGroups.forEach((ref) => {
      const label = ref.nativeElement.querySelector('.mat-mdc-optgroup-label');

      if (!label) {
        return;
      }

      label.addEventListener('click', this.onCheckboxLabelClick);
    });

    this.updateFilterDisabledState();

    this.filterGroup.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        tap(() => {
          this.updateFilterDisabledState();
        }),
      )
      .subscribe();

    this.cdr.detectChanges();
  }

  public ngAfterViewChecked(): void {
    // Rebinds the view on content repaint
    this.dataSource.sort = this.getSortView();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected filterHasValue(formControl: string) {
    const control = this.filterGroup.get(formControl);

    if (!control) {
      return false;
    }

    return !!control.value?.length;
  }

  protected clearIndividualFilter(event: MouseEvent, controlName: string) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const control = this.filterGroup.get(controlName);

    if (!control) {
      return;
    }

    control.setValue(void 0);
  }

  protected clearFilters(): void {
    this.store.dispatch(ResetAnalytics);
    this.filterGroup.reset();
    this.updateRouteParams();
  }

  protected updateFilters(form: FormGroupDirective): void {
    this.filters = Object.keys(this.filters)
      .map((key) => {
        const filter = this.filters[key as keyof AnalyticsFilters];

        if (!filter) {
          return {};
        }

        const formValue = form.value?.[key] || void 0;

        if (filter.type === InputType.Text) {
          if (key === 'revenueFrom') {
            return {
              [key]: {
                type: InputType.Text,
                value: this.filterGroup.get('revenue.start')?.value ?? '',
              },
            };
          }

          if (key === 'revenueTo') {
            return {
              [key]: {
                type: InputType.Text,
                value: this.filterGroup.get('revenue.end')?.value ?? '',
              },
            };
          }

          return { [key]: { ...filter, value: formValue ?? '' } };
        }

        if (filter.type === InputType.Multiselect) {
          const formControl = this.filterGroup.get(key);
          return { [key]: { ...filter, value: formControl?.value ?? [] } };
        }

        if (filter.type === InputType.DateRange) {
          let { dateStart = '', dateEnd = '' } = formValue || {};
          dateStart = formatAsDateTimeStringIgnoringTheTime(dateStart);
          dateEnd = formatAsDateTimeStringIgnoringTheTime(dateEnd);
          return { [key]: { ...filter, value: { dateStart, dateEnd } } };
        }

        return {};
      })
      .reduce((acc: {}, next: {}) => ({ ...acc, ...next }), {}) as unknown as AnalyticsFilters;

    this.updateRouteParams();
  }

  protected onPageChange(event: PageEvent): void {
    if (!this.analyticsViewData?.length) {
      return;
    }

    this.pageNumber = event.pageIndex;
    const startIndex = this.pageNumber * this.pageSize;
    this.dataSource.data = this.analyticsViewData.slice(startIndex, startIndex + this.pageSize);
  }

  protected onTableViewChange(ev: MatChipListboxChange) {
    const { value } = ev;

    if (!value) {
      return;
    }

    this.viewTable = value;
    this.analyticsViewData = this.analyticsTableData?.[this.viewTable] ?? [];
    this.dataSource.data = this.analyticsViewData.slice(0, this.pageSize);

    // Initialize paginator
    this.totalEntries = this.analyticsViewData?.length ?? 0;
    this.pageNumber = 0;
  }

  protected onChipSelectionChange(event: MatChipSelectionChange) {
    const value = event.source.value;

    if (this.viewTable === value && !event.selected) {
      event.source.selected = true;
      return;
    }

    return;
  }

  // Handler that takes the date input, changes the date of the month depending on the input type:
  // For start dates picks the first day of the month
  // For end dates picks the last day of the month
  protected chosenMonthHandler(date: Date, datepicker: MatDatepicker<Date>, reference: string) {
    // periodGroup -> timeperiod1, timeperiod2
    // dateType -> dateStart, dateEnd
    const [periodGroup, dateType] = reference.split('.');

    // Shift date to first or last date of the month
    let changedDate: Date;
    if (dateType === 'dateStart') {
      changedDate = new Date(date.getFullYear(), date.getMonth(), 1);
    } else {
      changedDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    const filterPeriodGroup = this.filterGroup.get(periodGroup);
    const groupDateValue = filterPeriodGroup?.get(dateType);

    groupDateValue?.setValue(changedDate);
    datepicker.close();
  }

  private onCheckboxLabelClick(ev: MouseEvent) {
    const { target } = ev;

    if (!target) {
      return;
    }

    const checkbox = (target as HTMLSpanElement).querySelector('.continent-header mat-checkbox');

    if (!checkbox) {
      return;
    }

    (checkbox as HTMLFormElement).click();
  }

  protected onContinentCheckboxChanged(
    continent: {
      name: Continent;
      countries: Country[];
      continentAdjustedCountries: string[];
    },
    checkbox: MatCheckbox,
  ): void {
    const { checked } = checkbox;
    const countryControl = this.filterGroup.get('countryCodes')!;
    const continentControl = this.filterGroup.get('continentCodes')!;
    const selectedCountries: Country[] = countryControl.value || [];
    const selectedContinents: Continent[] = continentControl.value || [];

    if (checked) {
      // Select all countries
      const newCountries = [...new Set([...selectedCountries, ...continent.continentAdjustedCountries])];
      countryControl.setValue(newCountries);

      if (!selectedContinents.includes(continent.name)) {
        continentControl.setValue([...selectedContinents, continent.name]);
      }
    } else {
      // Deselect all countries
      const newCountries = selectedCountries.filter((c) => !continent.continentAdjustedCountries.includes(c));
      countryControl.setValue(newCountries);

      continentControl.setValue(selectedContinents.filter((c) => c !== continent.name));
    }
  }

  protected onCountrySelectionChange(event: MatSelectChange<string[]>): void {
    const adjustedCountries: string[] = event.value;
    this.countryCodesControl.setValue(adjustedCountries.map((code) => code as Country));

    const continentControl = this.filterGroup.get('continentCodes')!;
    const updatedContinents: string[] = [];

    for (const continent of this.continents) {
      const countriesInContinent = continent.continentAdjustedCountries;
      const allCountriesInCurrentContinentSelected = countriesInContinent.every((c) => adjustedCountries.includes(c));

      if (allCountriesInCurrentContinentSelected) {
        updatedContinents.push(continent.name);
      }
    }

    continentControl.setValue(updatedContinents);
  }

  protected isContinentFullySelected(continent: {
    countries: Country[];
    continentAdjustedCountries: string[];
  }): boolean {
    const selectedCountries = this.filterGroup.controls['countryCodes'].value || [];
    return continent.continentAdjustedCountries.every((c) => selectedCountries.includes(c));
  }

  protected isContinentIndeterminate(continent: {
    countries: Country[];
    continentAdjustedCountries: string[];
  }): boolean {
    const selectedCountries = this.filterGroup.controls['countryCodes'].value || [];
    const selectedCount = continent.continentAdjustedCountries.filter((c) => selectedCountries.includes(c)).length;
    return selectedCount > 0 && selectedCount < continent.continentAdjustedCountries.length;
  }

  // Redirects to the incidents page applying all filters from the analytics dashboard and the given time period
  protected redirectToIncidentsPageWithFilters(timePeriod: AnalyticsTimePeriod): void {
    const incidentsPageParams = mapAnalyticsFiltersToIncidentsPageRouteParams(this.filters, timePeriod);

    const urlTree = this.router.createUrlTree(['/incidents'], { queryParams: incidentsPageParams });
    window.open(this.urlSerializer.serialize(urlTree), '_blank');
  }

  // Redirects to the incidents page applying one period dates and a business sector
  protected redirectToIncidentsPageFromBusinessSectorView(
    businessSector: string,
    datePeriod: AnalyticsTimePeriod,
  ): void {
    const incidentsPageParams = mapAnalyticsFiltersToIncidentsPageRouteParams(this.filters, datePeriod);
    incidentsPageParams['businessSector'] = businessSector;

    const urlTree = this.router.createUrlTree(['/incidents'], { queryParams: incidentsPageParams });
    window.open(this.urlSerializer.serialize(urlTree), '_blank');
  }

  // Redirects to the incidents page applying all filters but changing the geo location field to the country represented by the row in the table.
  protected redirectToIncidentsPageFromCountryView(location: string, datePeriod: AnalyticsTimePeriod): void {
    const incidentsPageParams = mapAnalyticsFiltersToIncidentsPageRouteParams(this.filters, datePeriod);
    incidentsPageParams['geoLocation'] = location;

    const urlTree = this.router.createUrlTree(['/incidents'], { queryParams: incidentsPageParams });
    window.open(this.urlSerializer.serialize(urlTree), '_blank');
  }

  // Redirects to the incidents page applying all filters and replacing the geo location with filtered countries:
  // If we have selected countries ['China', 'France'] we want to assign only ['France'] if the input continent in this method is 'Europe'
  protected redirectToIncidentsPageFromContinentView(continent: Continent, datePeriod: AnalyticsTimePeriod): void {
    const incidentsPageParams = mapAnalyticsFiltersToIncidentsPageRouteParams(this.filters, datePeriod);

    // Get all countries in the selected continent
    const countriesInContinent = CountriesByContinent[continent];

    if (this.filters.countryCodes.value.length === 0) {
      // If there are no specified countries in the filters, we need to apply the whole continent in the geo location
      incidentsPageParams['geoLocation'] = continent;
    } else {
      // Filter selected countries to only those in the target continent
      const filteredCountries = new Set(
        this.filters.countryCodes.value.flatMap((code) => {
          const countryCode = code.split(':')[1];
          const country = countryCode as Country;
          return countriesInContinent.has(country) ? countryCode : [];
        }),
      );

      incidentsPageParams['geoLocation'] =
        filteredCountries.size !== 0 ? Array.from(filteredCountries).join(',') : undefined;
    }

    const urlTree = this.router.createUrlTree(['/incidents'], { queryParams: incidentsPageParams });
    window.open(this.urlSerializer.serialize(urlTree), '_blank');
  }

  private patchFilterGroup(): void {
    this.filterGroup.patchValue(
      {
        timeperiod1: {
          dateStart: createDate(this.filters.timeperiod1.value.dateStart),
          dateEnd: createDate(this.filters.timeperiod1.value.dateEnd),
        },
        timeperiod2: {
          dateStart: createDate(this.filters.timeperiod2.value.dateStart),
          dateEnd: createDate(this.filters.timeperiod2.value.dateEnd),
        },
        revenue: {
          start: this.filters.revenueFrom.value,
          end: this.filters.revenueTo.value,
        },
        entitySizes: this.filters.entitySizes.value ?? [],
        businessSectors: this.filters.businessSectors.value ?? [],
        countryCodes: this.filters.countryCodes.value,
        continentCodes: this.filters.continentCodes.value,
      },
      { emitEvent: false },
    );

    this.viewPeriods.rangeA = `${formatAsYearAndMonthOnlyString(this.filterGroup?.get('timeperiod1')?.get('dateStart')?.value)} - ${formatAsYearAndMonthOnlyString(this.filterGroup?.get('timeperiod1')?.get('dateEnd')?.value)}`;
    this.viewPeriods.rangeB = `${formatAsYearAndMonthOnlyString(this.filterGroup?.get('timeperiod2')?.get('dateStart')?.value)} - ${formatAsYearAndMonthOnlyString(this.filterGroup?.get('timeperiod2')?.get('dateEnd')?.value)}`;
  }

  private updateFilterDisabledState() {
    const dSA = this.filterGroup.get('timeperiod1')?.get('dateStart')?.value;
    const dEA = this.filterGroup.get('timeperiod1')?.get('dateEnd')?.value;
    const dSB = this.filterGroup.get('timeperiod2')?.get('dateStart')?.value;
    const dEB = this.filterGroup.get('timeperiod2')?.get('dateEnd')?.value;
    this.filterButtonDisabled = !dSA || !dEA || !dSB || !dEB;
  }

  private getSortView(): MatSort {
    switch (this.viewTable) {
      case 'entity':
        return this.entitySort as MatSort;
      case 'country':
        return this.countrySort as MatSort;
      case 'continent':
        return this.continentSort as MatSort;
      default:
        return this.entitySort as MatSort;
    }
  }

  private updateRouteParams(): void {
    const routeParams = mapAnalyticsFiltersToRouteParams(this.filters);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: routeParams,
      queryParamsHandling: 'replace',
    });
  }
}
